import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Storage } from '@capacitor/storage';

const useSearchBar = (configs) => {
  const { name, maxRecentSearches = 3 } = configs;
  const [recentSearches, setRecentSearches] = useState([]);
  const isMounted = useRef(true);
  if (!name) {
    throw new Error("useSearchBar needs a localStorage name");
  }
  const getRecentSearches = useCallback(async () => {
    try {
      const { value: lastSearchesString } = await Storage.get({ key: name });
      const lastSearches = lastSearchesString ? JSON.parse(lastSearchesString) : [];
      return lastSearches;
    } catch (e) {
      return [];
    }
  }, [name]);
  const saveSearchLocal = useCallback(
    async (search) => {
      if (maxRecentSearches <= 0) {
        return;
      }
      try {
        const { value: recentSearchesString } = await Storage.get({
          key: name
        });
        if (!recentSearchesString) {
          const searchArray = [search];
          await Storage.set({
            key: name,
            value: JSON.stringify(searchArray)
          });
          setRecentSearches(searchArray);
          return;
        }
        const recentSearches2 = JSON.parse(recentSearchesString);
        if (recentSearches2.includes(search))
          return;
        if (recentSearches2.length >= maxRecentSearches) {
          recentSearches2.pop();
        }
        recentSearches2.unshift(search);
        setRecentSearches(recentSearches2);
        await Storage.set({
          key: name,
          value: JSON.stringify(recentSearches2)
        });
      } catch (e) {
        throw new Error(`Cannot set ${name} in Storage`);
      }
    },
    [maxRecentSearches, name]
  );
  const cleanSearchHistory = useCallback(async () => {
    try {
      await Storage.remove({ key: name });
      setRecentSearches([]);
    } catch (e) {
      throw new Error(`Cannot remove ${name} from Storage`);
    }
  }, [name]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    (async () => {
      const lastSearches = await getRecentSearches();
      if (!isMounted.current)
        return;
      setRecentSearches(lastSearches);
    })();
  }, [getRecentSearches]);
  return useMemo(
    () => ({
      cleanSearchHistory,
      saveSearchLocal,
      recentSearches
    }),
    [cleanSearchHistory, recentSearches, saveSearchLocal]
  );
};
useSearchBar.defaultProps = {
  config: {
    maxRecentSearches: 3
  }
};

export { useSearchBar };
//# sourceMappingURL=bundle.esm.js.map
